import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export function PageHeader({
  title,
  subtitle,
  backTo,
}: {
  title: string;
  subtitle?: string;
  backTo?: string;
}) {
  return (
    <div className="mb-5">
      {backTo && (
        <Link
          to={backTo}
          className="mb-2 inline-flex items-center gap-1 text-sm text-secondary hover:text-accent"
        >
          <ArrowLeft className="h-4 w-4" />
          Geri
        </Link>
      )}
      <h2 className="font-serif text-2xl font-semibold text-primary">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      <div className="gold-divider mt-3" />
    </div>
  );
}

export function InfoBox({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'ornate-card rounded-xl p-4',
        className,
      )}
    >
      <h3 className="font-serif text-base font-semibold text-primary">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

export function MemberPicker({
  members,
  selectedIds,
  onChange,
  filterTag,
}: {
  members: { id: string; fullName: string; status?: string; tags?: string[] }[];
  selectedIds: string[];
  /* Verilmezse liste OKUNUR hâlde: görevin kimin üstünde olduğu
     görünür ama değiştirilemez. Katılımcı görev dağıtamaz. */
  onChange?: (ids: string[]) => void;
  filterTag?: string;
}) {
  let approved = members.filter((m) => !m.status || m.status === 'approved');
  if (filterTag) {
    approved = approved.filter((m) => m.tags?.includes(filterTag));
  }

  if (approved.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {filterTag
          ? `"${filterTag}" etiketli katılımcı yok. Katılımcılar sayfasından etiket atayın.`
          : 'Henüz onaylı katılımcı yok. Davet linki ile katılım sağlayın.'}
      </p>
    );
  }

  const secili = approved.filter((m) => selectedIds.includes(m.id)).length;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-primary">Sorumlu Kişi(ler)</p>
        {onChange && (
          <p className="text-xs text-muted-foreground">
            {secili > 0 ? `${secili} kişi seçildi` : 'Kimse seçilmedi'}
          </p>
        )}
      </div>
      {/* Seçim çoklu: dokunulan kişi listeye ekleniyor, tekrar
          dokununca çıkıyor. Kutucuk ve tik olmadan bu anlaşılmıyordu. */}
      {onChange && (
        <p className="text-xs text-muted-foreground">
          Birden fazla kişi seçebilirsiniz; seçiliyi kaldırmak için tekrar dokunun.
        </p>
      )}
      <div className="flex flex-col gap-2">
        {approved.map((member) => {
          const selected = selectedIds.includes(member.id);
          return (
            <button
              key={member.id}
              type="button"
              disabled={!onChange}
              aria-pressed={selected}
              onClick={() =>
                onChange?.(
                  selected
                    ? selectedIds.filter((id) => id !== member.id)
                    : [...selectedIds, member.id],
                )
              }
              className={cn(
                'flex items-start gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                selected
                  ? 'border-accent bg-accent/15 text-primary'
                  : 'border-border bg-white text-muted-foreground',
                onChange ? 'hover:border-accent/50' : 'cursor-default',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                  selected ? 'border-accent bg-accent text-white' : 'border-border bg-white',
                )}
              >
                {selected && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="font-medium">{member.fullName}</span>
                {member.tags && member.tags.length > 0 && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {member.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
                      >
                        {tag}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
